<script>
export default {
  title: 'Project 0: Setting up your environment',
  starterId: 'proj0.zip'
}
</script>

<template>
  <section>
    <article>
      <h2>Submission</h2>
      <p>Nothing needs to be submitted for this project. However, it is strongly suggested
        for you to complete this assignment.</p>
    </article>

    <article>
      <h2>Starter Code</h2>

      <p>
        Please download the starter code from above. It should contains a single file
        called <code>setup.py</code>.
      </p>
    </article>

    <article>
      <h2 id="introduction">Introduction</h2>

      <p>This project explains how to use your own computer to complete assignments for CS
        9H. If you are using a lab computer, most of the instructions are the same,
        except you won't have to install anything.</p>

      <p>If you need any help at any time, you are welcomed to come in during our regular
        office hour and ask any one of our tutors.</p>
    </article>

    <article>
      <h2>Register for an account</h2>

      <blockquote><p>These accounts allow you to use instructional machines in the CS department,
        which can be useful if you do not have regular access to a computer. They are
        <strong>not required if you do not plan on using the lab computers or printers</strong>.</p></blockquote>

      <p>Go to the <a href="https://acropolis.cs.berkeley.edu/~account/webacct/">EECS account site</a>
        to register for an instructional account. Login using your Berkeley CalNet ID
        and click the <code>Get a new account</code> button in the row for CS 9H. Your username
        will be of the form cs9h-xxx. Write down or download your account form so you
        don't forget it!</p>

      <p>If you decide to use your instructional account to complete your course work, then you can save
        the effort of setting up a local environment on your own computer. You will still need to install
        a terminal, but then you can use SSH to connect to your instructional account and use SFTP to
        transfer files. However, we still recommend you to at least attempt all steps listed below to set
        up your own environment so that you may use it in the future.</p>

    </article>

    <article>
      <h2 id="setup">Setup</h2>

      <blockquote>
        <p>
          In this class, we encourage you <b>NOT</b> to use software like <code>Anaconda</code> or
          <code>Jupyter Notebook</code>, which will help you set up the environment. Instead,
          please use a standard terminal and a text editor to complete all your work.
        </p>
      </blockquote>

      <h3 id="install-a-terminal">Install a terminal</h3>

      <p>The terminal is a program that allows you to interact with your computer
        by entering commands. No matter what operating system you use (Windows,
        macOS, Linux), the terminal will be an essential tool for CS 9H.</p>

      <h4 id="terminal-macos-linux">macOS/Linux</h4>

      <p>If you're on a Mac or are using a form of Linux (such as Ubuntu), you already
        have a program called <code>Terminal</code> or something similar on your computer.  Open
        that up and you should be good to go.</p>

      <h4 id="terminal-windows">Windows</h4>

      <p>For Windows users, we recommend downloading a terminal called <a href="http://git-scm.com/downloads">Git
        Bash</a>.</p>

      <p>You should be able to install Git Bash with most of the default configuration
        options, with one exception. In the <em>Configuring the terminal emulator to use
          with Git Bash</em> step, select the second option: <em>Use Windows' default console
          window</em>.</p>

      <p><strong>This is very important! If you do not select this option, your terminal will
        not work!</strong></p>

      <figure>
        <img src="./images/setup_git_bash_options.png" class="img-responsive center-block"
             alt="Git Bash configuration options" width="600">
      </figure>

      <h3 id="install-python-3">Install Python 3</h3>

      <p>Python 3 is the primary programming language used in this course. Use the appropriate
        download link and additional instructions below to install the Python 3 interpreter.</p>

      <div class="table-responsive">
        <table class="table table-bordered">

          <tbody><tr>
            <th>OS</th>
            <th>Download</th>
          </tr>
          <tr>
            <td>Windows</td>
            <td><a href="https://www.python.org/ftp/python/3.7.0/python-3.7.0.exe">Installer</a></td>
          </tr>
          <tr>
            <td>macOS</td>
            <td><a href="https://www.python.org/ftp/python/3.7.0/python-3.7.0-macosx10.6.pkg">Installer</a></td>
          </tr>
          <tr>
            <td>Ubuntu</td>
            <td><code>sudo apt-get install python3</code></td>
          </tr>
          <tr>
            <td>Other</td>
            <td><a href="https://www.python.org/downloads/release/python-362/">Python3 Download</a></td>
          </tr>

          </tbody></table>

        <p>
          If the links above are not working for your computer, you may also visit the
          <a href="https://www.python.org/downloads/">official python website</a> for downloading Python 3.
        </p>
      </div>

      <h4 id="python-macos">macOS</h4>

      <p>Refer to this <a href="https://www.youtube.com/watch?v=smHuBHxJdK8">video</a> for additional help on setting
        up Python (the video features a slightly older version of Python 3, but the
        steps are still the same).</p>

      <p>You may need to right-click the download icon and select "Open". After installing
        please close and open your Terminal.</p>

      <h4 id="python-windows">Windows</h4>

      <p>When installing, make sure to check the "Add Python 3.6 to PATH"
        box, which will allow you to execute the <code>python</code> command from your terminal.</p>

      <figure>
        <img class="img-responsive center-block" src="./images/setup_python-windows-install-path.png" alt="Windows PATH at setup">
      </figure>

      <p>After installing please close and open your Terminal.</p>

      <h3 id="install-a-text-editor">Install a text editor</h3>

      <p>The <strong>Python interpreter</strong> that you just installed allows you to <em>run</em> Python
        code. You will also need a <strong>text editor</strong>, where you will <em>write</em> Python code.</p>

      <p>There are many editors out there, each with its own set of features. We find
        that <a href="https://atom.io/">Atom</a> and <a href="http://www.sublimetext.com/3">Sublime Text 3</a> are popular choices among students,
        but you are free to use other text editors.</p>

      <blockquote><p><strong>Note</strong>: Please, please, <em>please</em> do not use word processors such as
        Microsoft Word to edit programs.</p></blockquote>

      <p>For your reference, we've also written some guides on using popular text
        editors.</p>

      <ul>
        <li><a href="https://cs61a.org/articles/atom.html" target="_blank">Atom</a></li>
        <li><a href="https://cs61a.org/articles/sublime.html" target="_blank">Sublime Text 3</a></li>
        <li><a href="https://cs61a.org/articles/emacs.html" target="_blank">Emacs</a></li>
        <li><a href="https://cs61a.org/articles/vim.html" target="_blank">Vim</a></li>
      </ul>

      <p>
        In the future, when you start to build more complex projects, you may find IDEs, such as PyCharm, become
        more useful. However, they are not recommended for this course.
      </p>

      <h2 id="using-the-terminal">Using the terminal</h2>

      <p>Let's check if everything was installed properly! First, open a new terminal window,
        if you haven't already.</p>

      <figure>
        <img class="img-responsive center-block" src="./images/setup_terminal.png" alt="starting the terminal">
      </figure>

      <p>When you first open your terminal, you will start in the home directory.  The
        <strong>home directory</strong> is represented by the <code>~</code> symbol.</p>

      <blockquote><p>Don't worry if your terminal window doesn't look exactly the same; the
        important part is that the text on the left-hand side of the <code>$</code> has a
        <code>~</code> (tilde). That text might also have the name of your computer.</p></blockquote>

      <h4 id="python-interpreter">Python Interpreter</h4>

      <p>We can use the terminal to check if your Python 3 interpreter was installed
        correctly. Try thet following command:</p>

      <pre v-highlightjs><code class="bash">python3</code></pre>

      <p>If the installation worked, you should see some text printed out about the
        interpreter followed by <code>&gt;&gt;&gt;</code> on its own line. This is where you can type in Python
        code. Try typing some expressions you saw in lecture, or just play around to see
        what happens! You can type <code>exit()</code> or <code>Ctrl-D</code> to return to your command line.</p>

      <blockquote><p>If you are using Windows and the <code>python3</code> command doesn't work, try using just
        <code>python</code> or <code>py</code>. If neither of those work, make sure you set up your PATH correctly
        as shown above. Ask for help if you get stuck!</p></blockquote>

    </article>

    <article>
      <h2 id="organizing-your-files">Organizing your files</h2>

      <p>In this section, you will learn how to manage files using terminal commands.</p>

      <h3 id="directories">Directories</h3>

      <p>The first command you'll use is <code>ls</code>. Try typing it in the terminal:</p>

      <pre v-highlightjs><code class="bash">ls</code></pre>

      <p>The <code>ls</code> command <strong>l</strong>i<strong>s</strong>ts all the files and folders in the current
        directory. A <strong>directory</strong> is another name for a folder (such as the
        <code>Documents</code> folder). Since you're in the home directory right now, you
        should see the contents of your home directory.</p>

      <h3 id="changing-directories">Changing directories</h3>

      <p>To move into another directory, use the <code>cd</code> command. Let's try moving into your
        Desktop directory. First, make sure you're in your home directory (check for the
        <code>~</code> on your command line) and use <code>ls</code> to see if the <code>Desktop</code> directory is present.
        Try typing the following command into your terminal, which should move you into
        that directory:</p>

      <pre v-highlightjs><code class="bash">cd Desktop</code></pre>

      <p>If your desktop directory is not located within your home directory and you can't
        find it, ask a tutor for help.</p>

      <p>There are a few ways to return to the home directory:</p>

      <ul>
        <li><code>cd ..</code> (two dots). The <code>..</code> means "the parent directory". In
          this case, the parent directory of <code>cs9h</code> is your home
          directory, so you can use <code>cd ..</code> to go up one directory.</li>
        <li><code>cd ~</code> (the tilde). Remember that <code>~</code> means home directory, so
          this command will always change to your home directory.</li>
        <li><code>cd</code> (<code>cd</code> on its own). Typing just <code>cd</code> is a shortcut for typing <code>cd ~</code>.</li>
      </ul>

      <blockquote><p>You do not have to keep your files on your Desktop if you prefer otherwise.
        Where you keep your files locally will not affect your projects. Do whatever is
        easiest and most convenient for you!</p></blockquote>

      <h3 id="making-new-directories">Making new directories</h3>

      <p>The next command is called <code>mkdir</code>, which <strong>m</strong>a<strong>k</strong>es new
        <strong>dir</strong>ectories. Let's make a directory called <code>cs9h</code> on your Desktop to store
        all of the assignments for this class:</p>

      <pre v-highlightjs><code class="bash">mkdir cs9h</code></pre>

      <p>A folder named <code>cs9h</code> will appear on your Desktop. You can verify this by
        using the <code>ls</code> command again or by simply checking your Desktop.</p>

      <p>At this point, let's create some more directories. First, make sure you are in
        the <code>~/Desktop/cs9h</code> directory. Then, create a folder called <code>projects</code>
        inside of your <code>cs9h</code> folder:</p>

      <pre v-highlightjs><code class="bash">cd ~/Desktop/cs9h
mkdir projects</code></pre>

      <p>Now if you list the contents of the directory (using <code>ls</code>), you'll see the
        folder you just created, <code>projects</code>.</p>

      <figure>
        <img class="img-responsive center-block" src="./images/setup_terminal_commands.png" alt="cs9h directory">
      </figure>

      <h3 id="downloading-the-assignment">Downloading the assignment</h3>

      <p>If you haven't already, download the zip archive, <code>proj0.zip</code>, which
        contains all the files that you'll need for this project. Once you've done that, let's
        find the downloaded file. On most computers, <code>proj0.zip</code> is probably located in a
        directory called <code>Downloads</code> in your home directory. Use the <code>ls</code> command to
        check:</p>

      <pre v-highlightjs><code class="bash">ls ~/Downloads</code></pre>

      <p>If you don't see <code>proj0.zip</code>, ask a tutor for help.</p>

      <h3 id="extracting-starter-files">Extracting starter files</h3>

      <p>You must expand the zip archive before you can work on the project files.
        Different operating systems and different browsers have different ways of
        unzipping. If you don't know how, you can search online.</p>

      <blockquote><p>Using a terminal, you can unzip the zip file from the command line.
        First, <code>cd</code> into the directory that contains the zip file:</p>

        <pre v-highlightjs><code class="bash">cd ~/Downloads</code></pre>

        <p>Now, run the <code>unzip</code> command with the name of the zip file:</p>

        <pre v-highlightjs><code class="bash">unzip proj0.zip</code></pre>

        <p>You might also be able to unzip files without using the terminal by double
          clicking them in your OS's file explorer.</p></blockquote>

      <p>Once you unzip <code>proj0.zip</code>, you'll have a new folder called <code>proj0</code> which
        contains the following files (check it out with <code>cd</code> and <code>ls</code>):</p>

      <ul>
        <li><code>setup.py</code>: The template file you'll be adding your code to</li>
      </ul>

      <h3 id="moving-files">Moving files</h3>

      <p>Move the project files to the project folder you created earlier:</p>

      <pre v-highlightjs><code class="bash">mv ~/Downloads/proj0 ~/Desktop/cs9h/projects</code></pre>

      <p>The <code>mv</code> command will <strong>m</strong>o<strong>v</strong>e the <code>~/Downloads/proj0</code> folder
        into the <code>~/Desktop/cs9h/projects</code> folder.</p>

      <p>Now, go to the <code>proj0</code> folder that you just moved. Try using <code>cd</code> to navigate
        your own way! If you get stuck, you can use the following command:</p>

      <pre v-highlightjs><code class="bash">cd ~/Desktop/cs9h/projects/proj0</code></pre>

      <h3 id="summary">Summary</h3>

      <p>Here is a summary of the commands we just went over for your reference:</p>

      <ul>
        <li><code>ls</code>: <strong>l</strong>i<strong>s</strong>ts all files in the current directory</li>
        <li><code>cd &lt;path to directory&gt;</code>: <strong>c</strong>hange into the specified <strong>d</strong>irectory</li>
        <li><code>mkdir &lt;directory name&gt;</code>: <strong>m</strong>a<strong>k</strong>e a new <strong>dir</strong>ectory with the given name</li>
        <li><code>mv &lt;source path&gt; &lt;destination path&gt;</code>: <strong>m</strong>o<strong>v</strong>e the file at the given source to the
          given destination</li>
      </ul>

      <p>Finally, you're ready to start editing the project files! Don't worry if this seems
        complicated -- it will get much easier over time. Just keep practicing!</p>
    </article>

    <article>
      <h2 id="doing-the-assignment">Doing the assignment</h2>

      <h3 id="understanding-the-question">Understanding the question</h3>

      <p>Open up <code>setup.py</code> in your text editor. You can type <code>open .</code> on MacOS or <code>start .</code>
        on Windows to open the current directory in your Finder/File Explorer. Then
        double click or right click to open the file in your text editor. You should
        see something like this:</p>

      <pre v-highlightjs><code class="python">def twenty_eighteen():
    """Come up with the most creative expression that evaluates to 2018,
    using only numbers and the +, *, and - operators.

    >>> twenty_eighteen()
    2018
    """
    return ______</code></pre>

      <p>The lines in the triple-quotes <code>"""</code> are called a <strong>docstring</strong>, which is a
        description of what the function is supposed to do. When writing code in 9H,
        you should always read the docstring!</p>

      <p>The lines that begin with <code>&gt;&gt;&gt;</code> are called <strong>doctests</strong>. Recall that when using the
        Python interpreter, you write Python expressions next to <code>&gt;&gt;&gt;</code> and the output is
        printed below that line. Doctests explain what the function does by showing actual
        Python code: "if we input this Python code, what should the expected output be?"</p>

      <p>In <code>twenty_eighteen</code>,</p>

      <ul>
        <li>The docstring tells you to "come up with the most creative expression that
          evaluates to 2018," but that you can only use numbers and arithmetic operators
          <code>+</code> (add), <code>*</code> (multiply), and <code>-</code> (subtract).</li>
        <li>The doctest checks that the function call <code>twenty_eighteen()</code> should
          return the number 2018.</li>
      </ul>

      <blockquote><p>You generally will not need to modify the docstring, unless you want to add
        your own tests!
        The only part of your assignments that you'll need to edit is the code.</p></blockquote>

      <h3 id="writing-code">Writing code</h3>

      <p>Once you understand what the question is asking, you're ready to start writing
        code! You should replace the underscores in <code>return ______</code> with an expression that
        evaluates to 2018.  What's the most creative expression you can come up with?</p>

      <blockquote><p>Don't forget to save your assignment after you edit it! You can save by navigating
        to File &gt; Save or by pressing Command-S on MacOS or Ctrl-S on Windows.</p></blockquote>

      <h3 id="running-tests">Running tests</h3>

      <p>Back to the terminal! Make sure you are in the <code>proj0</code> directory we created
        earlier (remember, the <code>cd</code> command lets you <a href="#changing-directories">change
          directories</a>).</p>

      <p>In that directory, you can type <code>ls</code> to verify that there are the following
        three files:</p>

      <ul>
        <li><code>setup.py</code>: the starter file you just edited</li>
      </ul>

      <p>Now, let's test our code to make sure it works. You can run <code>doctests</code> with this
        command:</p>

      <pre v-highlightjs><code class="bash">python3 -m doctest setup.py</code></pre>

      <p>
        If all tests have passed, there should be no additional messages. Otherwise, it will show
        something like this:
      </p>

      <pre v-highlightjs><code class="plain">1 items had failures:
   1 of   1 in proj0.twenty_eighteen
***Test Failed*** 1 failures.</code></pre>

      <p>In the future, we will ask you to write doctests by yourself and test your programs before submitting them.</p>

      <h2 id="appendix-useful-python-command-line-options">Appendix: Useful Python command line options</h2>

      <p>When running a Python file, you can use <strong>options</strong> on the command line
        to inspect your code further. Here are a few that will come in handy.
        If you want to learn more about other Python command-line options, take a look
        at the <a href="https://docs.python.org/3.4/using/cmdline.html">documentation</a>.</p>

      <ul>
        <li><p>Using no command-line options will run the code in the file you
          provide and return you to the command line.</p>

          <pre v-highlightjs><code class="bash">python3 setup.py</code></pre></li>
        <li><p><strong><code>-i</code></strong>: The <code>-i</code> option runs your Python script, then opens an interactive
          session. In an interactive session, you run Python code line by line and get
          immediate feedback instead of running an entire file all at once. To exit,
          type <code>exit()</code> into the interpreter prompt. You can also use the keyboard
          shortcut <code>Ctrl-D</code> on Linux/Mac machines or <code>Ctrl-Z  Enter</code> on Windows.</p>

          <p>If you edit the Python file while running it interactively, you will need to
            exit and restart the interpreter in order for those changes to take effect.</p>

          <pre v-highlightjs><code class="bash">python3 -i setup.py</code></pre></li>
        <li><p><strong><code>-m doctest</code></strong>: Runs doctests in a particular file. Doctests are
          surrounded by triple quotes (<code>"""</code>) within functions. Each test consists of
          <code>&gt;&gt;&gt;</code> followed by some Python code and the expected output.</p>

          <pre v-highlightjs><code class="bash">python3 -m doctest setup.py</code></pre></li>
      </ul>
    </article>

  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/python/Setup.vue